import { z } from 'zod'
import { datatypeEnum, chartEnum, userRoleEnum } from './enums'

// Form input, not API responses.
//
// The opposite discipline applies here to the rest of lib/schemas: response
// schemas are permissive because the backend is a peer service that adds and
// nulls fields (R3); form schemas are strict because the input is authored by
// a person and the whole point is to reject what they got wrong (R4).
//
// Messages are user-facing copy — they render directly under the field.
//
// NOTE: `requiredString` and `email` both `.trim()`, which is a *transform* —
// submitted values are whitespace-trimmed where they previously were not.
// Intentional, but it is a behaviour change, not just a validation one.

const requiredString = (label: string) => z.string().trim().min(1, `${label} is required`)

const email = z
  .string()
  .trim()
  .min(1, 'Email is required')
  .pipe(z.email('Please enter a valid email address'))

// --- Auth -----------------------------------------------------------------

export const LoginFormSchema = z.object({
  email,
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
})
export type LoginFormValues = z.infer<typeof LoginFormSchema>

// The "forgot password" flow validates the email on its own, before there is a
// password to check. Sharing the field definition keeps one email rule in the
// codebase — it used to be an inline regex duplicated in two places on the
// login page.
export const EmailOnlySchema = z.object({ email })

// --- Locations & zones ----------------------------------------------------

export const LocationFormSchema = z.object({
  name: requiredString('Name'),
  description: z.string(),
  parent: z.string().nullable(),
})
// `LocationFormData` is the historical name used across the dialogs and
// use-api; it is this schema, not a second declaration of the same shape.
export type LocationFormData = z.infer<typeof LocationFormSchema>

export const ZoneFormSchema = z.object({
  name: requiredString('Name'),
  description: z.string(),
  engines: z.array(z.string()),
  sensors: z.array(z.string()),
  compteurs: z.array(z.string()),
})
export type ZoneFormData = z.infer<typeof ZoneFormSchema>

// --- Devices --------------------------------------------------------------

export const SchemaFieldFormSchema = z.object({
  name: requiredString('Field name'),
  slug: requiredString('Slug'),
  datatype: datatypeEnum,
  required: z.boolean(),
  description: z.string().optional(),
  group: z.string(),
  chart: chartEnum,
  unit: z.string().nullish(),
  is_energy_field: z.boolean().optional(),
})

export const DeviceFormSchema = z.object({
  device_name: requiredString('Device name'),
  device_type: requiredString('Device type'),
  reference: z.string().optional(),
  // Location UUID picked from the tree, or null for "unassigned" — the API
  // takes a Location pk here, not free text.
  location: z.string().nullable().optional(),
  description: z.string().optional(),
  schema_fields: z.array(SchemaFieldFormSchema),
})
export type DeviceFormValues = z.infer<typeof DeviceFormSchema>

// --- Users ----------------------------------------------------------------

export const UserFormSchema = z.object({
  name: requiredString('Name'),
  email,
  // Optional because the edit dialog reuses this form and leaves the password
  // blank to mean "unchanged"; when supplied it still has to be usable.
  password: z
    .union([
      // Blank means "leave unchanged" in the edit dialog.
      z.literal(''),
      z.string().min(6, 'Password must be at least 6 characters'),
    ])
    .optional(),
  role: userRoleEnum,
  phone: z.string().optional(),
})
export type UserFormValues = z.infer<typeof UserFormSchema>

// --- Profile --------------------------------------------------------------

export const ProfileFormSchema = z.object({
  first_name: requiredString('First name'),
  last_name: requiredString('Last name'),
})
export type ProfileFormValues = z.infer<typeof ProfileFormSchema>

export const ChangeEmailFormSchema = z
  .object({
    new_email: email,
    re_new_email: z.string().min(1, 'Please confirm the new email'),
    current_password: z.string().min(1, 'Current password is required'),
  })
  // Cross-field check — the reason this form needs a resolver rather than
  // per-field rules. The error is attached to the confirmation field so it
  // renders next to the input the user has to correct.
  .refine((d) => d.new_email === d.re_new_email, {
    message: 'Email addresses do not match',
    path: ['re_new_email'],
  })
export type ChangeEmailFormValues = z.infer<typeof ChangeEmailFormSchema>

export const ChangePasswordFormSchema = z
  .object({
    current_password: z.string().min(1, 'Current password is required'),
    new_password: z.string().min(6, 'Password must be at least 6 characters'),
    re_new_password: z.string().min(1, 'Please confirm the new password'),
  })
  .refine((d) => d.new_password === d.re_new_password, {
    message: 'Passwords do not match',
    path: ['re_new_password'],
  })
export type ChangePasswordFormValues = z.infer<typeof ChangePasswordFormSchema>

// --- AI models ------------------------------------------------------------

export const AIModelFormSchema = z.object({
  name: requiredString('Model name'),
  description: z.string().optional(),
  targetDeviceId: requiredString('Target device'),
  selectedFieldSlugs: z.array(z.string()).min(1, 'Select at least one field'),
  runningFrequency: z.string(),
})
export type AIModelFormValues = z.infer<typeof AIModelFormSchema>

// --- Settings tabs --------------------------------------------------------
// Local/mock UI state rather than API shapes, but validated for the same
// reason: they are typed into by a person.

export const ProjectSchema = z.object({
  projectId: z.string(),
  name: requiredString('Project name'),
  description: z.string().optional(),
  status: z.enum(['active', 'archived']),
})

export const CompanySettingsSchema = z.object({
  companyName: requiredString('Company name'),
  companyEmail: email,
  companyPhone: z.string(),
  projects: z.array(ProjectSchema),
  activeProjectId: z.string().optional(),
})
export type CompanySettingsValues = z.infer<typeof CompanySettingsSchema>

export const SystemConfigurationSchema = z.object({
  dataRetentionDays: z.string(),
  autoBackup: z.boolean(),
  emailNotifications: z.boolean(),
  maintenanceMode: z.boolean(),
  language: z.enum(['en', 'fr', 'es']),
  theme: z.enum(['system', 'light', 'dark']),
  dashboardRefresh: z.number(),
})
export type SystemConfigurationValues = z.infer<
  typeof SystemConfigurationSchema
>
